import * as React from 'react';
import { CollapsibleCard } from '@insolech/ui';
import type {
  ForefootState,
  ForefootWedgeMod,
  ForefootWedgeType,
  ForefootWedgeCorrection,
  ForefootHardness,
  ForefootExtensionMod,
  ForefootExtensionType,
  ForefootExtensionThickness,
  ForefootExtensionRigidity,
  ForefootExtensionSoftness,
  GaitPlateOption,
  FirstRayCutOutOption,
} from './prescriptionState';
import {
  ModCard,
  SideRow,
  SelectSm,
  NumInput,
  FieldLabel,
  SectionObservation,
} from './shared';
import { CARD_IDS } from './cardIds';
import { forefootExtensionTypesFor, forefootWedgeTypesFor } from './forefootRules';
import type { UnavailableLookup } from '../prescriptionLegality';

/**
 * ForefootSection (R30 rebuild).
 *
 * Card layout:
 *   Row A (full width): Forefoot wedge - combined Type/Correction/Height
 *     [+Hardness when extrinsic], per side. Replaces the R29 split between
 *     "Forefoot supinating wedge" + "Forefoot pronating wedge".
 *   Row B (30/70): Gait plate · Morton extension
 *   Row C (30/70): 1st Ray/MTPJ Cut Out · 2nd-5th metatarsal extension
 */

interface Props {
  value: ForefootState;
  onChange: (next: ForefootState) => void;
  /** Sandal-only branch (R37). When true, hides Gait plate, 1st
   *  Ray/MTPJ Cut Out and 2nd-5th metatarsal extension cards; prunes
   *  Forefoot wedge to None + intrinsic; locks Morton extension to
   *  intrinsic + semi-rigid only. */
  isSandal?: boolean | undefined;
  readOnly?: boolean | undefined;
  defaultOpen?: boolean | undefined;
  open?: boolean | undefined;
  onOpenChange?: ((next: boolean) => void) | undefined;
  /** Values this product does not offer, keyed by the checker's field key, so
   *  a control can mark ITSELF as the one the red banner is complaining about.
   *  See indexUnavailable in ../prescriptionLegality. */
  unavailable?: UnavailableLookup | undefined;
}

export const WEDGE_TYPES: ReadonlyArray<{ value: ForefootWedgeType; label: string }> = [
  { value: 'none', label: 'None' },
  { value: 'intrinsic', label: 'Forefoot post (intrinsic)' },
  { value: 'extrinsic', label: 'Forefoot EVA post (extrinsic)' },
];

export const WEDGE_CORRECTIONS: ReadonlyArray<{ value: ForefootWedgeCorrection; label: string }> = [
  { value: 'supinating', label: 'Supinating wedge' },
  { value: 'pronating', label: 'Pronating wedge' },
];

export const HARDNESS_OPTIONS: ReadonlyArray<{ value: ForefootHardness; label: string }> = [
  { value: 'shore_a_35', label: '35° Shore A' },
  { value: 'shore_a_65', label: '65° Shore A' },
];

/** Extension Type uses different labels than the wedge: 'EVA post' instead
 *  of 'Extrinsic (EVA post)' per Rafael's spec. Underlying values stay
 *  'intrinsic' / 'extrinsic' so the schema is shared. */
export const EXTENSION_TYPES: ReadonlyArray<{ value: ForefootExtensionType; label: string }> = [
  { value: 'none', label: 'None' },
  { value: 'intrinsic', label: 'Intrinsic' },
  { value: 'extrinsic', label: 'EVA post' },
];

export const EXTENSION_THICKNESSES: ReadonlyArray<{
  value: ForefootExtensionThickness;
  label: string;
}> = [
  { value: '1mm', label: '1mm' },
  { value: '2mm', label: '2mm' },
  { value: '3mm', label: '3mm' },
];

/** Intrinsic extensions use a rigidity scale (built into the shell).
 *  R34 renamed the tiers: Rigid / Semi-rigid / Semi-flexible. */
export const EXTENSION_RIGIDITY_OPTIONS: ReadonlyArray<{
  value: ForefootExtensionRigidity;
  label: string;
}> = [
  { value: 'rigid', label: 'Rigid' },
  { value: 'semi_rigid', label: 'Semi-rigid' },
  { value: 'semi_flexible', label: 'Semi-flexible' },
];

/** Thickness availability is gated by rigidity (intrinsic only):
 *   Rigid          → 2mm, 3mm  (too thin = breaks under load)
 *   Semi-rigid     → 1mm, 2mm, 3mm  (full range)
 *   Semi-flexible  → 1mm, 2mm  (too thick = no flex left)
 *  Extrinsic posts ignore this map — all three thicknesses always allowed. */
export const INTRINSIC_THICKNESS_BY_RIGIDITY: Record<
  ForefootExtensionRigidity,
  ReadonlyArray<ForefootExtensionThickness>
> = {
  rigid: ['2mm', '3mm'],
  semi_rigid: ['1mm', '2mm', '3mm'],
  semi_flexible: ['1mm', '2mm'],
  // Legacy 'flexible' draft → treat as 'semi_flexible' bounds.
  flexible: ['1mm', '2mm'],
};

/** Remap pre-R34 rigidity so legacy drafts hydrate without a blank select. */
function normalizeRigidity(
  r: ForefootExtensionRigidity | undefined,
): ForefootExtensionRigidity | undefined {
  if (r === 'flexible') return 'semi_flexible';
  return r;
}

export const GAIT_OPTIONS: ReadonlyArray<{ value: GaitPlateOption; label: string }> = [
  { value: 'none', label: 'None' },
  { value: 'external', label: 'External gait plate' },
  { value: 'internal', label: 'Internal gait plate' },
];

export const CUTOUT_OPTIONS: ReadonlyArray<{ value: FirstRayCutOutOption; label: string }> = [
  { value: 'none', label: 'None' },
  { value: '1st_ray', label: '1st Ray Cut Out' },
  { value: '1st_mtpj', label: '1st MTPJ Cut Out' },
];

/** Height range bounds per Type - intrinsic is built into the shell so it
 *  can go thicker (1-6mm); extrinsic is glued on so it stays 1-3mm. */
export const HEIGHT_BOUNDS: Record<Exclude<ForefootWedgeType, 'none'>, { min: number; max: number }> = {
  intrinsic: { min: 1, max: 6 },
  extrinsic: { min: 1, max: 3 },
};

export function ForefootSection({
  value,
  onChange,
  isSandal = false,
  readOnly,
  defaultOpen = false,
  open,
  onOpenChange,
  unavailable,
}: Props) {
  const set = <K extends keyof ForefootState>(k: K, v: ForefootState[K]) =>
    onChange({ ...value, [k]: v });

  const wedge: ForefootWedgeMod = value.forefoot_wedge ?? {};
  const gp = value.gait_plate ?? {};
  const morton: ForefootExtensionMod = value.morton_extension ?? {};
  const cut = value.first_ray_cut_out ?? {};
  const met: ForefootExtensionMod = value.metatarsal_extension ?? {};

  const isControlled = open !== undefined && onOpenChange !== undefined;
  return (
    <CollapsibleCard
      title="Forefoot modifications"
      subtitle="Wedge, gait plate, extensions, cut-out"
      {...(isControlled
        ? { open: open!, onOpenChange: onOpenChange! }
        : { defaultOpen })}
    >
      <div className="space-y-3">
        {/* 1. Forefoot wedge - full width */}
        <ModCard
          id={CARD_IDS.forefootWedge}
          title="Forefoot wedge"
          subtitle="Type drives manufacturing approach and height range."
          image={{
            src: '/prescription/modifications/forefoot-wedge.png',
            alt: 'Forefoot wedge reference',
          }}
          info={
            <div className="max-w-[300px] text-xs leading-snug space-y-2">
              <p>
                <strong>Intrinsic</strong> = built into the shell (1-6mm).
                <br />
                <strong>Extrinsic</strong> = EVA post (1-3mm). Hardness
                picks 35° or 65° Shore A.
              </p>
              <p>
                <strong>Supinating wedge</strong> sits medially and
                supinates the foot. <strong>Pronating wedge</strong> sits
                laterally and pronates the foot.
              </p>
            </div>
          }
        >
          <WedgeRow
            side="L"
            data={wedge}
            patch={(p) => set('forefoot_wedge', p)}
            readOnly={readOnly}
            isSandal={isSandal}
            unavailable={unavailable}
          />
          <WedgeRow
            side="R"
            data={wedge}
            patch={(p) => set('forefoot_wedge', p)}
            readOnly={readOnly}
            isSandal={isSandal}
            unavailable={unavailable}
          />
        </ModCard>

        {/* Row B: Gait plate (30) + Morton extension (70). Gait plate is
            hidden for sandals (R37); Morton extension then spans the row. */}
        <div
          className={
            isSandal
              ? 'grid grid-cols-1 gap-3'
              : 'grid grid-cols-1 md:grid-cols-[2fr_3fr] gap-3'
          }
        >
          {!isSandal ? (
            <ModCard
              id={CARD_IDS.gaitPlate}
              title="Gait plate"
              subtitle="External or internal (reverse)"
              image={{
                src: '/prescription/modifications/gait-plate.png',
                alt: 'Gait plate reference',
              }}
            >
              <SideRow side="L">
                <SelectSm
                  value={gp.left}
                  onChange={(v) => set('gait_plate', { ...gp, left: v })}
                  options={GAIT_OPTIONS}
                  disabled={readOnly}
                  placeholder="Select"
                  className="min-w-[10rem]"
                />
              </SideRow>
              <SideRow side="R">
                <SelectSm
                  value={gp.right}
                  onChange={(v) => set('gait_plate', { ...gp, right: v })}
                  options={GAIT_OPTIONS}
                  disabled={readOnly}
                  placeholder="Select"
                  className="min-w-[10rem]"
                />
              </SideRow>
            </ModCard>
          ) : null}

          <ExtensionCard
            id={CARD_IDS.mortonExtension}
            title="Morton extension"
            subtitle="Extends to sulcus. Limits first MTPJ motion."
            imageSrc="/prescription/modifications/morton-extension.png"
            imageAlt="Morton extension reference"
            value={morton}
            onChange={(v) => set('morton_extension', v)}
            readOnly={readOnly}
            isSandal={isSandal}
            fieldKeyPrefix="morton_extension"
            unavailable={unavailable}
          />
        </div>

        {/* Row C: 1st Ray/MTPJ Cut Out (30) + 2nd-5th metatarsal extension (70).
            Both cards hidden for sandals (R37). */}
        {!isSandal ? (
        <div className="grid grid-cols-1 md:grid-cols-[2fr_3fr] gap-3">
          <ModCard
            id={CARD_IDS.firstRayCutOut}
            title="1st Ray/MTPJ Cut Out"
            subtitle="Localised cut-out for first ray or first MTPJ."
            image={{
              src: '/prescription/modifications/first-ray-cutout.png',
              alt: 'First ray cut-out reference',
            }}
          >
            <SideRow side="L">
              <SelectSm
                value={cut.left}
                onChange={(v) => set('first_ray_cut_out', { ...cut, left: v })}
                options={CUTOUT_OPTIONS}
                disabled={readOnly}
                placeholder="Select"
                className="min-w-[10rem]"
              />
            </SideRow>
            <SideRow side="R">
              <SelectSm
                value={cut.right}
                onChange={(v) => set('first_ray_cut_out', { ...cut, right: v })}
                options={CUTOUT_OPTIONS}
                disabled={readOnly}
                placeholder="Select"
                className="min-w-[10rem]"
              />
            </SideRow>
          </ModCard>

          <ExtensionCard
            id={CARD_IDS.metatarsalExtension}
            title="2nd-5th metatarsal extension"
            subtitle="Extends to sulcus. Redistributes pressure across the lesser metatarsals."
            imageSrc="/prescription/modifications/metatarsal-extension.png"
            imageAlt="2nd-5th metatarsal extension reference"
            value={met}
            onChange={(v) => set('metatarsal_extension', v)}
            readOnly={readOnly}
            fieldKeyPrefix="metatarsal_extension"
            unavailable={unavailable}
          />
        </div>
        ) : null}

        <SectionObservation
          value={value.observations}
          onChange={(v) => onChange({ ...value, observations: v })}
          disabled={readOnly}
          placeholder="Section-specific notes for forefoot…"
        />
      </div>
    </CollapsibleCard>
  );
}

// ---- Forefoot wedge row (full-width card, single side) ------------------

function WedgeRow({
  side,
  data,
  patch,
  readOnly,
  isSandal,
  unavailable,
}: {
  side: 'L' | 'R';
  data: ForefootWedgeMod;
  patch: (v: ForefootWedgeMod) => void;
  readOnly?: boolean | undefined;
  isSandal?: boolean | undefined;
  unavailable?: UnavailableLookup | undefined;
}) {
  const isLeft = side === 'L';
  const type = isLeft ? data.left_type : data.right_type;
  const correction = isLeft ? data.left_correction : data.right_correction;
  const height = isLeft ? data.left_height_mm : data.right_height_mm;
  const hardness = isLeft ? data.left_hardness : data.right_hardness;
  const bounds = type && type !== 'none' ? HEIGHT_BOUNDS[type] : undefined;

  const update = (changes: Partial<ForefootWedgeMod>) => patch({ ...data, ...changes });

  const setType = (v: ForefootWedgeType | undefined) => {
    if (!v || v === 'none') {
      // Clear all dependent fields when type goes back to None.
      if (isLeft) {
        update({
          left_type: v,
          left_correction: undefined,
          left_height_mm: undefined,
          left_hardness: undefined,
        });
      } else {
        update({
          right_type: v,
          right_correction: undefined,
          right_height_mm: undefined,
          right_hardness: undefined,
        });
      }
      return;
    }
    // Seed correction so the user immediately sees the follow-up fields.
    // Height also seeds on first reveal (to the type's lower bound) so
    // the conditionally-shown text box is never blank — matches the
    // Rearfoot wedge / Subcalcaneal offloading precedent.
    if (isLeft) {
      update({
        left_type: v,
        left_correction: data.left_correction ?? 'supinating',
        left_height_mm: clampHeight(data.left_height_mm, v) ?? HEIGHT_BOUNDS[v].min,
        // Drop hardness when leaving extrinsic; default Shore A 35 when
        // entering it.
        left_hardness:
          v === 'extrinsic' ? data.left_hardness ?? 'shore_a_35' : undefined,
      });
    } else {
      update({
        right_type: v,
        right_correction: data.right_correction ?? 'supinating',
        right_height_mm: clampHeight(data.right_height_mm, v) ?? HEIGHT_BOUNDS[v].min,
        right_hardness:
          v === 'extrinsic' ? data.right_hardness ?? 'shore_a_35' : undefined,
      });
    }
  };

  const setCorrection = (v: ForefootWedgeCorrection | undefined) =>
    update(isLeft ? { left_correction: v } : { right_correction: v });
  const setHeight = (v: number | undefined) =>
    update(isLeft ? { left_height_mm: v } : { right_height_mm: v });
  const setHardness = (v: ForefootHardness | undefined) =>
    update(isLeft ? { left_hardness: v } : { right_hardness: v });

  // Sandal mode (R37) hides the extrinsic EVA-post option. Rule shared with the
  // legality checker via ./forefootRules.
  const allowedWedgeTypes = forefootWedgeTypesFor(!!isSandal);
  const wedgeTypeOptions = WEDGE_TYPES.filter((t) => allowedWedgeTypes.includes(t.value));

  return (
    <SideRow side={side}>
      <SelectSm
        value={type}
        onChange={setType}
        options={wedgeTypeOptions}
        disabled={readOnly}
        placeholder="Type"
        className="min-w-[12rem]"
        unavailableLabel={
          unavailable?.[`forefoot_wedge_${isLeft ? 'left' : 'right'}_type`]?.valueLabel
        }
        invalid={!!unavailable?.[`forefoot_wedge_${isLeft ? 'left' : 'right'}_type`]}
      />
      {type && type !== 'none' ? (
        <>
          <SelectSm
            value={correction}
            onChange={setCorrection}
            options={WEDGE_CORRECTIONS}
            disabled={readOnly}
            placeholder="Correction"
            className="min-w-[11rem]"
          />
          <FieldLabel>Height</FieldLabel>
          <NumInput
            value={height}
            onChange={setHeight}
            min={bounds?.min ?? 1}
            max={bounds?.max ?? 6}
            disabled={readOnly}
            placeholder={bounds ? `${bounds.min}-${bounds.max}` : 'mm'}
            suffix="mm"
            width="w-20"
            invalid={height == null}
          />
          {height == null ? (
            <span className="text-[11px] font-medium text-error-500">Required</span>
          ) : null}
          {type === 'extrinsic' ? (
            <SelectSm
              value={hardness}
              onChange={setHardness}
              options={HARDNESS_OPTIONS}
              disabled={readOnly}
              placeholder="Hardness"
              className="min-w-[8rem]"
            />
          ) : null}
        </>
      ) : null}
    </SideRow>
  );
}

function clampHeight(
  current: number | undefined,
  type: ForefootWedgeType,
): number | undefined {
  if (current == null || type === 'none') return current;
  const { min, max } = HEIGHT_BOUNDS[type];
  return Math.min(max, Math.max(min, current));
}

// ---- Extension card (Morton + 2nd-5th metatarsal share this) ------------

interface ExtensionCardProps {
  title: string;
  subtitle: string;
  imageSrc: string;
  imageAlt: string;
  value: ForefootExtensionMod;
  onChange: (v: ForefootExtensionMod) => void;
  readOnly?: boolean | undefined;
  id?: string | undefined;
  isSandal?: boolean | undefined;
  /** Which extension this card is, so the row can look ITSELF up in the
   *  checker's result. This card is rendered twice with different state
   *  (Morton and 2nd-5th), and the two are NOT interchangeable: the checker
   *  skips the 2nd-5th on sandals because the card is not rendered there.
   *  Without the prefix both cards would read the same key and one would
   *  wear the other's error. */
  fieldKeyPrefix?: 'morton_extension' | 'metatarsal_extension' | undefined;
  unavailable?: UnavailableLookup | undefined;
}

function ExtensionCard({
  title,
  subtitle,
  imageSrc,
  imageAlt,
  value,
  onChange,
  readOnly,
  id,
  isSandal,
  fieldKeyPrefix,
  unavailable,
}: ExtensionCardProps) {
  return (
    <ModCard
      id={id}
      title={title}
      subtitle={subtitle}
      image={{ src: imageSrc, alt: imageAlt }}
    >
      <ExtensionRow side="L" data={value} patch={onChange} readOnly={readOnly} isSandal={isSandal} fieldKeyPrefix={fieldKeyPrefix} unavailable={unavailable} />
      <ExtensionRow side="R" data={value} patch={onChange} readOnly={readOnly} isSandal={isSandal} fieldKeyPrefix={fieldKeyPrefix} unavailable={unavailable} />
    </ModCard>
  );
}

function ExtensionRow({
  side,
  data,
  patch,
  readOnly,
  isSandal,
  fieldKeyPrefix,
  unavailable,
}: {
  side: 'L' | 'R';
  data: ForefootExtensionMod;
  patch: (v: ForefootExtensionMod) => void;
  readOnly?: boolean | undefined;
  isSandal?: boolean | undefined;
  fieldKeyPrefix?: 'morton_extension' | 'metatarsal_extension' | undefined;
  unavailable?: UnavailableLookup | undefined;
}) {
  const isLeft = side === 'L';
  const type = isLeft ? data.left_type : data.right_type;
  const thickness = isLeft ? data.left_thickness : data.right_thickness;
  const rawSoftness = isLeft ? data.left_softness : data.right_softness;
  // Remap legacy 'flexible' on read so the SelectSm shows a matching label.
  const softness: ForefootExtensionSoftness | undefined =
    rawSoftness === 'flexible' ? 'semi_flexible' : rawSoftness;

  const update = (changes: Partial<ForefootExtensionMod>) => patch({ ...data, ...changes });

  /** Default softness depends on Type: rigidity for intrinsic, Shore A for
   *  extrinsic. Used both when the user picks a Type and when switching
   *  between Types (to reset a stale value). Sandal mode locks intrinsic
   *  to semi_rigid - the only allowed rigidity for sandal extensions. */
  const defaultSoftness = (t: ForefootExtensionType): ForefootExtensionSoftness => {
    if (t === 'intrinsic') return isSandal ? 'semi_rigid' : 'rigid';
    return 'shore_a_35';
  };

  const isSoftnessForType = (
    v: ForefootExtensionSoftness | undefined,
    t: ForefootExtensionType,
  ): v is ForefootExtensionSoftness => {
    if (v == null) return false;
    if (t === 'intrinsic')
      return v === 'rigid' || v === 'semi_rigid' || v === 'semi_flexible' || v === 'flexible';
    if (t === 'extrinsic') return v === 'shore_a_35' || v === 'shore_a_65';
    return false;
  };

  const setType = (v: ForefootExtensionType | undefined) => {
    if (!v || v === 'none') {
      if (isLeft) {
        update({ left_type: v, left_thickness: undefined, left_softness: undefined });
      } else {
        update({ right_type: v, right_thickness: undefined, right_softness: undefined });
      }
      return;
    }
    // Seed defaults so the follow-ups render immediately. Reset softness
    // if the existing value belongs to the other Type's option set.
    // Sandal mode forces intrinsic rigidity to semi_rigid regardless of
    // any prior pick (e.g. legacy 'rigid' from a non-sandal draft).
    const seededSoftness: ForefootExtensionSoftness =
      isSandal && v === 'intrinsic'
        ? 'semi_rigid'
        : isSoftnessForType(softness, v)
          ? softness!
          : defaultSoftness(v);
    // Thickness must respect the rigidity gate for intrinsic types.
    const allowedThickness =
      v === 'intrinsic' && seededSoftness in INTRINSIC_THICKNESS_BY_RIGIDITY
        ? INTRINSIC_THICKNESS_BY_RIGIDITY[seededSoftness as ForefootExtensionRigidity]
        : (['1mm', '2mm', '3mm'] as const);
    const seededThickness: ForefootExtensionThickness =
      thickness && allowedThickness.includes(thickness)
        ? thickness
        : v === 'extrinsic' && allowedThickness.includes('2mm' as ForefootExtensionThickness)
          ? ('2mm' as ForefootExtensionThickness)
          : (allowedThickness[0] as ForefootExtensionThickness);
    if (isLeft) {
      update({
        left_type: v,
        left_thickness: seededThickness,
        left_softness: seededSoftness,
      });
    } else {
      update({
        right_type: v,
        right_thickness: seededThickness,
        right_softness: seededSoftness,
      });
    }
  };

  const setThickness = (v: ForefootExtensionThickness | undefined) =>
    update(isLeft ? { left_thickness: v } : { right_thickness: v });

  /** Switching rigidity may narrow the thickness set (e.g. Rigid → no 1mm).
   *  Clamp the current thickness to the first allowed value if it's no
   *  longer in range, so the visible Thickness select stays valid. */
  const setSoftness = (v: ForefootExtensionSoftness | undefined) => {
    if (type !== 'intrinsic' || !v || !(v in INTRINSIC_THICKNESS_BY_RIGIDITY)) {
      update(isLeft ? { left_softness: v } : { right_softness: v });
      return;
    }
    const allowed = INTRINSIC_THICKNESS_BY_RIGIDITY[v as ForefootExtensionRigidity];
    const nextThickness =
      thickness && allowed.includes(thickness) ? thickness : (allowed[0] as ForefootExtensionThickness);
    if (isLeft) {
      update({ left_softness: v, left_thickness: nextThickness });
    } else {
      update({ right_softness: v, right_thickness: nextThickness });
    }
  };

  // The softness column's options swap based on Type. Intrinsic uses the
  // rigidity scale; extrinsic uses Shore A hardness. Sandal mode (R37)
  // locks intrinsic rigidity to semi_rigid only.
  const softnessOptions =
    type === 'intrinsic'
      ? isSandal
        ? EXTENSION_RIGIDITY_OPTIONS.filter((o) => o.value === 'semi_rigid')
        : EXTENSION_RIGIDITY_OPTIONS
      : type === 'extrinsic'
        ? HARDNESS_OPTIONS
        : [];

  // Sandal mode hides the extrinsic EVA-post Type option entirely. Rule shared
  // with the legality checker via ./forefootRules.
  const allowedExtensionTypes = forefootExtensionTypesFor(!!isSandal);
  const extensionTypeOptions = EXTENSION_TYPES.filter((t) =>
    allowedExtensionTypes.includes(t.value),
  );

  // Thickness options narrow when the rigidity (intrinsic) restricts them.
  const thicknessOptions = (() => {
    if (type === 'intrinsic') {
      const rigidity = normalizeRigidity(softness as ForefootExtensionRigidity | undefined);
      if (rigidity && rigidity in INTRINSIC_THICKNESS_BY_RIGIDITY) {
        const allowed = INTRINSIC_THICKNESS_BY_RIGIDITY[rigidity];
        return EXTENSION_THICKNESSES.filter((t) => allowed.includes(t.value));
      }
    }
    return EXTENSION_THICKNESSES;
  })();

  const softnessPlaceholder = type === 'intrinsic' ? 'Rigidity' : 'Hardness';

  return (
    <SideRow side={side}>
      <SelectSm
        value={type}
        onChange={setType}
        options={extensionTypeOptions}
        disabled={readOnly}
        placeholder="Type"
        className="w-24"
        // Guarded on the prefix: without it this would read `undefined_left_type`
        // from the lookup, which is never a key, so the mark simply would not
        // appear. Silent, and exactly the drift this threading exists to stop.
        unavailableLabel={
          fieldKeyPrefix
            ? unavailable?.[`${fieldKeyPrefix}_${isLeft ? 'left' : 'right'}_type`]?.valueLabel
            : undefined
        }
        invalid={
          !!fieldKeyPrefix &&
          !!unavailable?.[`${fieldKeyPrefix}_${isLeft ? 'left' : 'right'}_type`]
        }
      />
      {type && type !== 'none' ? (
        <>
          {/* Rigidity / Hardness comes BEFORE thickness so the user can
              pick the manufacturing characteristic first and the thickness
              options narrow to what's physically reasonable for that pick. */}
          <SelectSm
            value={softness as ForefootHardness | ForefootExtensionRigidity | undefined}
            onChange={(v) => setSoftness(v as ForefootExtensionSoftness | undefined)}
            options={
              softnessOptions as ReadonlyArray<{ value: string; label: string }>
            }
            disabled={readOnly}
            placeholder={softnessPlaceholder}
            className="w-28"
          />
          <SelectSm
            value={thickness}
            onChange={setThickness}
            options={thicknessOptions}
            disabled={readOnly}
            placeholder="Thickness"
            className="w-16"
          />
        </>
      ) : null}
    </SideRow>
  );
}
